import { AsyncPipe, DatePipe, NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PrimeTemplate } from 'primeng/api';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { DrawerModule } from 'primeng/drawer';
import { SelectModule } from 'primeng/select';
import { Subscription } from 'rxjs';
import { ExcelService } from 'src/app/core/services/excel.service';
import { NewFreelancerRequestService } from 'src/app/main/home-freelancers/new-freelancer-request/new-freelancer-request.service';
import { ExcelRequestColumn } from 'src/app/models/ExcelRequestColumn';
import { FilterResponse } from 'src/app/models/FilterResponse';
import { QrCodeDialogComponent } from 'src/app/shared/components/qr-code-dialog/qr-code-dialog.component';
import { LoadingComponent } from 'src/app/utils/loading/loading.component';
import { UtilsService } from 'src/app/utils/utils.service';
import { ColumnFilterDirective } from '../../../shared/directives/p-columnFilter.directive';
import { TableDirective } from '../../../shared/directives/p-table.directive';
import { FreelancerRequestDetailService } from '../freelancer-request-detail/freelancer-request-detail.service';
import { NewFreelancerRequestComponent } from '../new-freelancer-request/new-freelancer-request.component';
import { FreelancerRequestService } from './freelancer-request.service';
import { FreelancerRequestsActiveFiltersComponent } from './freelancer-requests-active-filters/freelancer-requests-active-filters.component';
import { FreelancerRequestsFilterService } from './freelancer-requests-filter.service';

@Component({
    selector: 'app-freelancer-requests',
    templateUrl: './freelancer-requests.component.html',
    standalone: true,
    imports: [
        NgIf,
        DrawerModule,
        PrimeTemplate,
        NewFreelancerRequestComponent,
        FormsModule,
        ReactiveFormsModule,
        TableModule,
        TableDirective,
        TooltipModule,
        FreelancerRequestsActiveFiltersComponent,
        IconFieldModule,
        InputIconModule,
        InputTextModule,
        ColumnFilterDirective,
        SelectModule,
        DatePickerModule,
        NgFor,
        SkeletonModule,
        TranslateModule,
        AsyncPipe,
        DatePipe,
        LoadingComponent,
        QrCodeDialogComponent
    ]
})
export class RequestsComponent implements OnInit, OnDestroy {

    isLoading = false;
    filterResponse: FilterResponse;
    sidebarVisible = false;
    private filterHandlerSubscription!: Subscription;
	@ViewChild('table') table: Table;

	constructor(
        private readonly router: Router,
		public readonly freelancerRequestsFilterService: FreelancerRequestsFilterService,
		public readonly freelancerRequestService: FreelancerRequestService,
        public readonly freelancerRequestDetailService: FreelancerRequestDetailService,
		public readonly newFreelancerRequestService: NewFreelancerRequestService,
		public readonly utilsService: UtilsService,
        private readonly excelService: ExcelService,
        private readonly translate: TranslateService,
        private readonly datePipe: DatePipe
	) {
	    this.filterHandlerSubscription = this.freelancerRequestsFilterService.filterHandler().subscribe((filteredResponse: FilterResponse) => {
	        this.filterResponse = filteredResponse;
	    });
	}

	async ngOnInit(): Promise<void> {
	    await this.freelancerRequestsFilterService.search();
	}

	ngOnDestroy(): void {
	    if (this.filterHandlerSubscription) {
	        this.filterHandlerSubscription.unsubscribe();
	    }
	}

	goToDetail(queryId: string): void {
	    this.isLoading = true;
	    this.router.navigate([`/freelancers/request-detail/${queryId}`])
	        .catch(error => { console.error(error); })
	        .finally(() => this.isLoading = false);
	}

	async exportToExcel(): Promise<void> {
	    const cols: ExcelRequestColumn[] = [
	        {
	            key: 'queryId',
	            i18n: 'MY_REQUESTS.QUERY_ID',
	            width:36
	        },
	        {
	            key: 'queryType',
	            i18n: 'FILTER.REQUEST_TYPE',
	            value: (r) => r.queryType === 'PhysicalPerson' ? this.translate.instant('FILTER.QUERY_TYPES.PHYSICAL_PERSON') : r.queryType
	        },
	        {
	            key: 'personId',
	            i18n: 'MY_REQUESTS.ID',
	            width: 12
	        },
	        {
	            key: 'name',
	            i18n: 'FILTER.PERSON_ENTITY',
	            width: 34,
	            value: (r) => this.utilsService.getFullName(r)
	        },
	        {
	            key: 'email',
	            i18n: 'FILTER.EMAIL',
	            width: 22
	        },
	        {
	            key: 'phoneNumber',
	            i18n: 'FILTER.PHONE',
	            width: 12
	        },
	        {
	            key: 'createdDateTimeUTC',
	            i18n: 'MY_REQUESTS.REQUEST_DATE_UTC',
	            width: 22,
	            value: (r) => this.datePipe.transform(r.createdDateTime, 'dd/MM/yyyy HH:mm:ss', 'UTC')
	        },
	        {
	            key: 'statusi18n',
	            i18n: 'MY_REQUESTS.STATUS',
	            width: 14,
	            value: (r) => this.translate.instant(`STATUS.${r.status}`)
	        },
	        {
	            key: 'substatusi18n',
	            i18n: 'MY_REQUESTS.SUBSTATUS',
	            width: 34,
	            value: (r) => this.translate.instant(`SUBSTATUS.${r.substatus}`)
	        },
	        {
	            key: 'requestReference',
	            i18n: 'MY_REQUESTS.REQUEST_REFERENCE',
	            width: 24
	        }
	    ];

	    await this.excelService.exportList(this.filterResponse?.requestList, cols);
	}

	newRequest(): void {
	    this.newFreelancerRequestService.myForm.reset();
	    this.sidebarVisible = true;
	}

	globalSearch($event: Event): void {
	    const input = $event.target as HTMLInputElement;
	    this.freelancerRequestsFilterService.globalSearchSubject.next({ inputValue: input.value, inputFallBackReference: input });
	}

	checkFilterFieldHasValue(field: string): boolean {
	    return this.freelancerRequestsFilterService.form.get(field).value != null && this.freelancerRequestsFilterService.form.get(field).value != '';
	}

	showIfTableIsLoadingAndEmpty(): boolean {
	    return (this.filterResponse?.requestList?.length == 0 || this.filterResponse == null) && this.freelancerRequestsFilterService.isLoading;
	}

	onSortChange(event: any): void {
	    const field = event.field;
	    const order = event.order;
	    this.freelancerRequestsFilterService.setSortField(field, order);
	}

	async sync(): Promise<void> {
	    this.freelancerRequestsFilterService.resetSortField();
	    if (this.table) {
	        this.table.reset();	// refresh the table to show the new data
	    }
	    await this.freelancerRequestsFilterService.search(true);
	}
}
